import {
  SignupContactStep,
  SignupPersonalStep,
  type CustomerAuthFieldUiHelpers,
} from './form-sections'
import type { CustomerAuthFormProps } from './CustomerAuthForm.types'
import type { ReactNode } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { SignupSection } from './SignupSection'
import { SignupReviewSection } from './SignupReviewSection'

type CustomerAuthSignupStepContentProps = {
  signupStep: number
  form: CustomerAuthFormProps
  ui: CustomerAuthFieldUiHelpers
}

const resolveOptionLabel = (
  options: Array<{ value: string; label: string }>,
  value: string,
  fallback = '-',
) => options.find((entry) => entry.value === value)?.label ?? (value.trim() || fallback)

const normalizeValue = (value: string, fallback = '-') => value.trim() || fallback
const SignupReviewSummary = ({ form }: { form: CustomerAuthFormProps }) => {
  const phone = `${form.phonePrefix.trim()} ${form.phoneLocalNumber.trim()}`.trim()
  const gender = resolveOptionLabel(form.genderOptions, form.gender)
  const personalRows = [
    { label: form.firstNameLabel, value: normalizeValue(form.firstName) },
    { label: form.lastNameLabel, value: normalizeValue(form.lastName) },
    { label: form.taxCodeLabel, value: normalizeValue(form.taxCode) },
    { label: form.genderLabel, value: gender },
  ]
  const contactRows = [
    { label: form.emailLabel, value: normalizeValue(form.email) },
    { label: form.phoneNumberLabel, value: normalizeValue(phone) },
  ]
  const residenceRows = [
    { label: form.addressStreetLabel, value: normalizeValue(form.addressStreet) },
    { label: form.addressStreetNumberLabel, value: normalizeValue(form.addressStreetNumber) },
    { label: form.addressCityLabel, value: normalizeValue(form.addressCity) },
    { label: form.addressPostalCodeLabel, value: normalizeValue(form.addressPostalCode) },
    { label: form.addressProvinceLabel, value: normalizeValue(form.addressProvince) },
    { label: form.addressCountryLabel, value: normalizeValue(form.addressCountry) },
  ]
  return (
    <SignupReviewSection
      title={form.signupReviewTitleLabel}
      description={form.signupReviewDescriptionLabel}
      editLabel={form.onStepEditLabel}
      personalTitle={form.signupStepPersonalLabel}
      contactTitle={form.signupStepContactLabel}
      addressTitle={form.residentialAddressLabel}
      personalRows={personalRows}
      contactRows={contactRows}
      addressRows={residenceRows}
      onEditPersonal={() => form.onJumpToSignupStep(1)}
      onEditContact={() => form.onJumpToSignupStep(2)}
    />
  )
}

export const CustomerAuthSignupStepContent = ({ signupStep, form, ui }: CustomerAuthSignupStepContentProps) => {
  let content: ReactNode
  if (signupStep === 1) {
    content = (
      <SignupSection title={form.personalDataSectionLabel}>
        <SignupPersonalStep
          personalDataSectionLabel={form.personalDataSectionLabel}
          firstNameLabel={form.firstNameLabel}
          firstNamePlaceholder={form.firstNamePlaceholder}
          lastNameLabel={form.lastNameLabel}
          lastNamePlaceholder={form.lastNamePlaceholder}
          taxCodeLabel={form.taxCodeLabel}
          taxCodePlaceholder={form.taxCodePlaceholder}
          genderLabel={form.genderLabel}
          genderPlaceholder={form.genderPlaceholder}
          genderOptions={form.genderOptions}
          emailLabel={form.emailLabel}
          emailPlaceholder={form.emailPlaceholder}
          firstName={form.firstName}
          lastName={form.lastName}
          taxCode={form.taxCode}
          gender={form.gender}
          email={form.email}
          onFirstNameChange={form.onFirstNameChange}
          onLastNameChange={form.onLastNameChange}
          onTaxCodeChange={form.onTaxCodeChange}
          onGenderChange={form.onGenderChange}
          onEmailChange={form.onEmailChange}
          ui={ui}
        />
      </SignupSection>
    )
  } else if (signupStep === 2) {
    content = (
      <SignupSection title={form.signupStepContactLabel} subtitle={form.contactDataSectionLabel}>
        <SignupContactStep
          phoneSectionLabel={form.phoneSectionLabel}
          phonePrefixLabel={form.phonePrefixLabel}
          phonePrefixPlaceholder={form.phonePrefixPlaceholder}
          phonePrefixSearchPlaceholder={form.phonePrefixSearchPlaceholder}
          phonePrefixNoResultsLabel={form.phonePrefixNoResultsLabel}
          phonePrefixOptions={form.phonePrefixOptions}
          phoneNumberLabel={form.phoneNumberLabel}
          phonePlaceholder={form.phonePlaceholder}
          residentialAddressLabel={form.residentialAddressLabel}
          contactDataSectionLabel={form.contactDataSectionLabel}
          addressStreetLabel={form.addressStreetLabel}
          addressStreetPlaceholder={form.addressStreetPlaceholder}
          addressStreetNumberLabel={form.addressStreetNumberLabel}
          addressStreetNumberPlaceholder={form.addressStreetNumberPlaceholder}
          addressCityLabel={form.addressCityLabel}
          addressCityPlaceholder={form.addressCityPlaceholder}
          addressPostalCodeLabel={form.addressPostalCodeLabel}
          addressPostalCodePlaceholder={form.addressPostalCodePlaceholder}
          addressProvinceLabel={form.addressProvinceLabel}
          addressProvincePlaceholder={form.addressProvincePlaceholder}
          addressCountryLabel={form.addressCountryLabel}
          addressCountryPlaceholder={form.addressCountryPlaceholder}
          countrySearchPlaceholder={form.countrySearchPlaceholder}
          countryNoResultsLabel={form.countryNoResultsLabel}
          addressCountryOptions={form.addressCountryOptions}
          phonePrefix={form.phonePrefix}
          phoneLocalNumber={form.phoneLocalNumber}
          addressStreet={form.addressStreet}
          addressStreetNumber={form.addressStreetNumber}
          addressCity={form.addressCity}
          addressPostalCode={form.addressPostalCode}
          addressProvince={form.addressProvince}
          addressCountry={form.addressCountry}
          onPhonePrefixChange={form.onPhonePrefixChange}
          onPhoneLocalNumberChange={form.onPhoneLocalNumberChange}
          onAddressStreetChange={form.onAddressStreetChange}
          onAddressStreetNumberChange={form.onAddressStreetNumberChange}
          onAddressCityChange={form.onAddressCityChange}
          onAddressPostalCodeChange={form.onAddressPostalCodeChange}
          onAddressProvinceChange={form.onAddressProvinceChange}
          onAddressCountryChange={form.onAddressCountryChange}
          ui={ui}
        />
      </SignupSection>
    )
  } else if (signupStep === 3) {
    content = (
      <SignupSection title={form.signupSecurityTitleLabel} subtitle={form.signupSecurityDescriptionLabel}>
        <div className="rounded-lg bg-[color-mix(in_srgb,var(--color-bg-page-secondary)_82%,var(--color-bg-card))] px-4 py-3">
          <p className="text-sm leading-relaxed text-(--color-text-secondary)">{form.signupSecurityHintLabel}</p>
        </div>
      </SignupSection>
    )
  } else {
    content = <SignupReviewSummary form={form} />
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={signupStep}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
      >
        {content}
      </motion.div>
    </AnimatePresence>
  )
}
